import { useRef } from "react";

import { StoreLocatorCardList } from "@locators/components/shared/components/StoreLocatorCardList";
import { StoreLocatorMap } from "@locators/components/shared/components/StoreLocatorMap";
import { StoreLocatorSearchBarV2 } from "@locators/components/shared/components/StoreLocatorSearchBarV2";
import { ViewSwitch } from "@locators/components/shared/components/ViewSwitch";
import { useCSSProperty, useHeight, useStoreLocatorContext } from "@locators/components/shared/hooks";
import { useDisplayType } from "@locators/components/shared/hooks/useDisplayType";
import classNames from "classnames";

import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { StoreLocatorFiltersV2 } from "../StoreLocatorFiltersV2";

import styles from "@locators/components/shared/styles/LocatorLayout.module.scss";

export const StoreLocatorV2 = () => {
  const { __ } = useTranslations();
  const { isMapActive, isNoMatchingFlotiqStores } = useStoreLocatorContext();
  const { change, isList, isMap } = useDisplayType("map");
  const { ref: topBarRef, height } = useHeight<HTMLDivElement>();
  const storeRef = useCSSProperty("--storeLocatorTopBarHeight", isMapActive ? `-${height}px` : "unset", [
    height,
    isMapActive,
  ]);

  const mainRef = useRef<HTMLDivElement | null>(null);

  return (
    <div
      className={classNames(styles.storeLocator, { [styles.storeLocatorFixedHeight]: isMap })}
      ref={storeRef}
      translate="no" // disabling browser translation of the child elements as it breaks the page on mount/unmount of filters
    >
      <div className={classNames(styles.top, { [styles.hide]: isMapActive && isMap })} ref={topBarRef}>
        <div className={classNames(styles.wrapper)}>
          <StoreLocatorSearchBarV2 filtersSlot={<StoreLocatorFiltersV2 />} />
        </div>
      </div>

      <div className={classNames(styles.main)} ref={mainRef}>
        <div className={classNames(styles.wrapper, styles.desktopHidden)}>
          <ViewSwitch
            className={classNames(styles.viewSwitch, {
              [styles.viewSwitchOnMap]: isMap,
              [styles.fullSize]: isMapActive && isMap,
            })}
            onChangeResultType={change}
          />
        </div>
        <div className={styles.wrapper}>
          {isNoMatchingFlotiqStores && (
            <div className={styles.warningMessage}>
              <AlertBanner
                tone="warning"
                description={__("cart.checkout.delivery.in_store.attributes_filtering.no_matches_found")}
              />
            </div>
          )}
        </div>
        <div className={classNames(styles.wrapper, styles.grid)}>
          <div
            className={classNames({
              [styles.mobileHidden]: !isList,
            })}
          >
            <StoreLocatorCardList isMapView={isMap} mapWrapperRef={mainRef} />
          </div>
          <StoreLocatorMap className={classNames(styles.map, { [styles.mobileHidden]: !isMap })} />
        </div>
      </div>
    </div>
  );
};
